import React, { useCallback, useContext, useMemo } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { IItemForGetShortInfo, UserRoles, ITotalPrice } from 'types';
import { useRoleAccess } from 'hooks/useRoleAccess';
import MultipleSelect from './components/MultipleSelect';
import SelectedItem from 'components/SelectedItem/SelectedItem';
import style from './IsDisabledField.module.scss';

interface IIsDisabledField {
	isDisabledInRestaurants: string[];
	totalPrices: ITotalPrice[];
	fetchedRestaurants: IItemForGetShortInfo[];
	isCreatedInPos: boolean;
	setDisabledInRestaurants: (value: string[]) => void;
}

const IsDisabledField: React.FC<IIsDisabledField> = ({
	isDisabledInRestaurants,
	totalPrices,
	fetchedRestaurants,
	isCreatedInPos,
	setDisabledInRestaurants,
}) => {
	const { loggedUser } = useContext<IAppContext>(AppContext);
	const hasAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);

	const handleRemove = useCallback(
		(id: string) => {
			const updatedSelectedItems = isDisabledInRestaurants.filter((el) => el !== id);
			setDisabledInRestaurants(updatedSelectedItems);
		},
		[isDisabledInRestaurants, setDisabledInRestaurants],
	);

	const restaurantsList = useMemo(() => {
		return isDisabledInRestaurants.map((el) => {
			const accessibleRestaurantIds = loggedUser.restaurants?.map((el) => el.id);
			const label = fetchedRestaurants.find((fetched) => fetched.id === el)?.name || '';
			return (
				<SelectedItem
					key={el}
					label={label}
					onDelete={
						isCreatedInPos || !(hasAccess || accessibleRestaurantIds?.includes(el))
							? undefined
							: () => handleRemove(el)
					}
				/>
			);
		});
	}, [loggedUser, fetchedRestaurants, hasAccess, isCreatedInPos, handleRemove]);

	return (
		<div className={style.container} data-test="IsDisabledField">
			<div className={style.field}>
				<MultipleSelect
					isDisabledInRestaurants={isDisabledInRestaurants}
					totalPrices={totalPrices}
					isCreatedInPos={isCreatedInPos}
					setDisabledInRestaurants={setDisabledInRestaurants}
				/>
			</div>
			<div className={style.groupsList}>{restaurantsList}</div>
		</div>
	);
};

export default React.memo(IsDisabledField);
